/**
 * First-touch and last-touch attribution (tracker LEAD-02, LINK-12, LINK-13, GROW-10).
 *
 * One scheme for every capture and every checkout. On each page load (not each
 * route change) this reads the landing URL and the referrer once:
 *
 *   first   the first landing this browser ever recorded: utm_source,
 *           utm_medium, utm_campaign, utm_content, utm_term, the referrer and
 *           the landing path. Written once, never overwritten.
 *   last    the most recent landing that carried campaign tags or came from
 *           another site. A direct visit (typed URL, bookmark, our own pages)
 *           does not overwrite it, so a reader who clicked a newsletter link
 *           on Monday and came back directly on Wednesday is still credited to
 *           the newsletter. Kept 30 days.
 *
 * Both live in localStorage under af_attr. The UTM values use the same scheme
 * as the newsletter brand files (src/lib/newsletterBrands.ts tagged()):
 * utm_source = publication slug or site, utm_medium = email | web | social |
 * referral, utm_campaign = newsletter | welcome | <automation>, utm_content =
 * the block.
 *
 * Where it goes:
 *   - installAttribution() wraps supabase.functions.invoke so the capture and
 *     checkout functions listed in ATTRIBUTED_FUNCTIONS receive
 *     body.attribution = { v, first, last, offer } without each form having to
 *     remember. A body that already carries attribution is left alone.
 *     offer is the last offer placement clicked this visit (offers.ts), so a
 *     purchase can be credited to the placement and split-test arm.
 *   - The edge functions sanitise it again (supabase/functions/_shared/
 *     attribution.ts), store it on the row (attribution jsonb) and put it in
 *     Stripe Checkout metadata (ft_* and lt_* keys, utm_* = last touch).
 *   - Stripe Payment Links (the Playbook) take it as utm_* query params and a
 *     compact client_reference_id (playbookLinkWithAttribution).
 *
 * Privacy: campaign tags, the referrer's origin and path (never its query),
 * and our own landing path. No personal data. Nothing is stored when the
 * browser sends Do Not Track or Global Privacy Control, the same rule as
 * siteEvents. Every storage access is wrapped: blocked storage means no
 * attribution, never a broken form.
 */
import { supabase } from "@/integrations/supabase/client";
import { lastOfferClick } from "@/lib/offers";

export interface Touch {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  referrer?: string;
  landing_path?: string;
  at: string;
}

export interface Attribution {
  v: 1;
  first?: Touch;
  last?: Touch;
  offer?: { key?: string; surface?: string; variant?: string };
}

const KEY = "af_attr";
const LAST_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Edge functions that receive body.attribution automatically. */
export const ATTRIBUTED_FUNCTIONS = new Set([
  // captures
  "capture-job-applicant",
  "create-job-alert",
  "library-access",
  "waitlist-subscribe",
  "add-beehiiv-subscriber",
  "submit-lead",
  // checkouts
  "create-library-checkout",
  "create-playbook-checkout",
  "create-sprint-checkout",
  "create-job-seeker-checkout",
  "create-list-checkout",
  "create-directory-checkout",
  "create-anonymous-job-checkout",
]);

function optedOut(): boolean {
  if (typeof window === "undefined") return true;
  const n = navigator as Navigator & { globalPrivacyControl?: boolean };
  return n.doNotTrack === "1" || n.globalPrivacyControl === true;
}

/** utm values: lower case, a safe alphabet, 60 characters. Matches utmFrom() on the server. */
export function cleanTag(v: string | null | undefined): string | undefined {
  const t = (v ?? "").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  return t || undefined;
}

/** A referrer without its query string or hash (those can carry personal data). */
function cleanReferrer(raw: string): string | undefined {
  try {
    const u = new URL(raw);
    if (u.host === window.location.host) return undefined;
    return `${u.protocol}//${u.host}${u.pathname === "/" ? "" : u.pathname}`.slice(0, 300);
  } catch {
    return undefined;
  }
}

function read(): Attribution {
  try {
    const v = JSON.parse(window.localStorage.getItem(KEY) ?? "null") as Attribution | null;
    if (v && v.v === 1) return v;
  } catch { /* blocked or corrupt */ }
  return { v: 1 };
}

function write(a: Attribution) {
  try { window.localStorage.setItem(KEY, JSON.stringify(a)); } catch { /* blocked */ }
}

/** The landing URL as the app was opened, read once at module load: routes
 *  rewrite the URL (redirects, /jobs/<uuid> to /jobs/<slug>) before pages mount. */
const ENTRY = typeof window === "undefined" ? null : { search: window.location.search, path: window.location.pathname };

function captureLanding() {
  if (!ENTRY || optedOut()) return;
  try {
    const q = new URLSearchParams(ENTRY.search);
    const touch: Touch = {
      source: cleanTag(q.get("utm_source")),
      medium: cleanTag(q.get("utm_medium")),
      campaign: cleanTag(q.get("utm_campaign")),
      content: cleanTag(q.get("utm_content")),
      term: cleanTag(q.get("utm_term")),
      referrer: document.referrer ? cleanReferrer(document.referrer) : undefined,
      landing_path: (ENTRY.path || "/").slice(0, 200),
      at: new Date().toISOString(),
    };
    const a = read();
    if (!a.first) a.first = touch;
    // A landing with tags or from another site is a new last touch; a direct one is not.
    if (touch.source || touch.medium || touch.campaign || touch.referrer) a.last = touch;
    write(a);
  } catch { /* never break the page */ }
}
captureLanding();

/** The stored attribution, with an expired last touch dropped. Undefined when there is none. */
export function getAttribution(): Attribution | undefined {
  if (typeof window === "undefined" || optedOut()) return undefined;
  const a = read();
  if (a.last && Date.now() - Date.parse(a.last.at) > LAST_TTL_MS) delete a.last;
  const offer = lastOfferClick();
  const out: Attribution = { v: 1, first: a.first, last: a.last };
  if (offer.from_key || offer.from_surface) {
    out.offer = { key: offer.from_key, surface: offer.from_surface, variant: offer.from_variant };
  }
  return out.first || out.last || out.offer ? out : undefined;
}

/** The touch that best explains this visit: last touch, else first touch. */
export function currentTouch(): Touch | undefined {
  const a = getAttribution();
  return a?.last ?? a?.first;
}

/** utm_* query params for the current touch (Payment Links pass them to the success URL). */
export function utmParams(): Record<string, string> {
  const t = currentTouch();
  const out: Record<string, string> = {};
  if (t?.source) out.utm_source = t.source;
  if (t?.medium) out.utm_medium = t.medium;
  if (t?.campaign) out.utm_campaign = t.campaign;
  if (t?.content) out.utm_content = t.content;
  if (t?.term) out.utm_term = t.term;
  return out;
}

/**
 * Stripe Payment Links accept client_reference_id (letters, digits, - and _,
 * 200 characters) and copy it onto the Checkout Session, which is the only
 * place a Payment Link sale can carry its source. Format, read back by
 * parsePlaybookRef() in supabase/functions/_shared/attribution.ts:
 *   af1__<offer key>__<offer surface>__<variant>__<ft source>__<ft medium>__<ft campaign>__<lt source>__<lt medium>__<lt campaign>
 * Empty parts are "x". Any other character becomes "-", runs of "_" collapse
 * to one so a value never contains the "__" separator, and the variant's ":"
 * (apply_offer_v2:sprint) becomes its last "-", which the server turns back.
 */
export function compactRef(): string | undefined {
  const a = getAttribution();
  if (!a) return undefined;
  // Lengths keep the whole id within 200 characters (3 + 24 + 16 + 32 + 2 x (18 + 10 + 14) + 9 separators of 2
  // = 177) while fitting every publication slug (month-end-closers is 17) and test arm we use.
  const part = (v: string | undefined, n: number) => {
    const t = (v ?? "").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/_+/g, "_").slice(0, n).replace(/^[-_]+|[-_]+$/g, "");
    return t || "x";
  };
  const ref = [
    "af1",
    part(a.offer?.key, 24), part(a.offer?.surface, 16), part(a.offer?.variant, 32),
    part(a.first?.source, 18), part(a.first?.medium, 10), part(a.first?.campaign, 14),
    part(a.last?.source, 18), part(a.last?.medium, 10), part(a.last?.campaign, 14),
  ].join("__");
  return ref.slice(0, 200);
}

/** A Playbook Payment Link with the attribution on it: utm_* and client_reference_id. */
export function playbookLinkWithAttribution(url: string): string {
  try {
    const u = new URL(url);
    for (const [k, v] of Object.entries(utmParams())) u.searchParams.set(k, v);
    const ref = compactRef();
    if (ref) u.searchParams.set("client_reference_id", ref);
    return u.toString();
  } catch {
    return url;
  }
}

let installed = false;

/**
 * Add body.attribution to calls of the functions in ATTRIBUTED_FUNCTIONS.
 * supabase.functions is a getter that builds a new FunctionsClient on every
 * access, so the wrap goes on the prototype (the same pattern siteEvents uses).
 */
export function installAttribution() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  try {
    const proto = Object.getPrototypeOf(supabase.functions) as { invoke: (...a: unknown[]) => Promise<unknown> };
    const original = proto.invoke;
    proto.invoke = function (this: unknown, ...args: unknown[]) {
      try {
        const name = args[0];
        const opts = args[1] as { body?: unknown } | undefined;
        if (typeof name === "string" && ATTRIBUTED_FUNCTIONS.has(name) && opts && opts.body
            && typeof opts.body === "object" && !(opts.body instanceof FormData) && !(opts.body instanceof Blob)
            && !Array.isArray(opts.body) && !("attribution" in (opts.body as Record<string, unknown>))) {
          const attribution = getAttribution();
          if (attribution) args[1] = { ...opts, body: { ...(opts.body as Record<string, unknown>), attribution } };
        }
      } catch { /* attribution never blocks a call */ }
      return original.apply(this, args);
    };
  } catch { /* leave invoke untouched */ }
}
installAttribution();
